// Help articles, English (HELP_SYSTEM_PLAN.md, phase 1; drafted 2026-10-31 from the rules
// documents for the product owner's review). Each article belongs to a category and to
// the screens where it shows under "For this screen". Body blocks: { p } a paragraph,
// { steps } numbered steps, { list } bullet points, { note } a highlighted note.
// [text](#article-id) links to another article (product owner, 2026-10-31: wherever an
// article mentions something another article explains). The two practice modes are
// always "Guided Mode" and "Advanced Mode" ("live" in code). Example wording a candidate
// would say in an interview stays in English in every language.

export const CATEGORIES = [
  { id: "start", title: "Getting started" },
  { id: "setup", title: "Setting up an interview" },
  { id: "sound", title: "Microphone and sound" },
  { id: "during", title: "During the interview" },
  { id: "live", title: "Advanced Mode" },
  { id: "report", title: "Your report and scores" },
  { id: "account", title: "Account, sessions and privacy" },
  { id: "free", title: "Free trial" },
];

export const ARTICLES = [
  // ── Getting started ──────────────────────────────────────────────────────
  {
    id: "how-it-works", category: "start", screens: ["upload", "account"],
    title: "How the simulator works",
    body: [
      { p: "The English Job Interview Simulator lets you practise a real job interview in English and shows you exactly how to improve." },
      { steps: [
        "Upload [your résumé](#upload-resume) and the [job description](#job-description) for the role you want.",
        "Choose your practice mode: [Guided Mode or Advanced Mode](#guided-or-live).",
        "Answer the 10 interview questions out loud. The interviewer may ask a short [follow-up question](#follow-ups).",
        "Read [your report](#report-overview): your interview performance, your professional English feedback, and a [polished version of each answer](#polished-answer).",
      ] },
      { p: "The questions are built from your résumé and the job description, so every interview is different." },
    ],
  },
  {
    id: "app-language", category: "start", screens: ["account"],
    title: "Choosing your app language",
    body: [
      { p: "The app language is the language of the screens, instructions and feedback. You choose it when you sign up, and you can change it in Account Settings, on the menu bar at the upper right." },
      { p: "The interview itself is always in English: the questions, your answers and the [polished answers](#polished-answer). Explanations and tips are in your app language." },
      { p: "The app is available in English, Portuguese (Brazil), Spanish (Latin America) and French (Canada)." },
    ],
  },
  {
    id: "partner-code", category: "start", screens: ["account"],
    title: "Using a Partner Code",
    body: [
      { p: "A Partner Code gives you access, or discounted rates for access, through one of our partners. You can enter it when you sign up and create your account profile, or later in Account Settings." },
      { steps: [
        "Open Account Settings.",
        "Type the code exactly as you received it in the Partner Code field.",
        "Select Apply. The screen shows your new [access end date](#access-sessions).",
      ] },
      { note: "If the code is not accepted, check for spaces or mistyped letters. If it still doesn't work, contact the partner who gave you the code." },
    ],
  },
  {
    id: "sign-in", category: "start", screens: ["login"],
    title: "Signing in and resetting your password",
    body: [
      { p: "You can sign in with your email and password, or with your Google account if you signed up with Google." },
      { steps: [
        "To reset your password, select the Recover password tab at the top of the sign-in box.",
        "Enter the email address you signed up with.",
        "Open the email we send you and follow the link to choose a new password.",
      ] },
      { note: "No email received? Check your spam or junk folder, and make sure you typed the same address you signed up with." },
    ],
  },

  // ── Setting up an interview ──────────────────────────────────────────────
  {
    id: "upload-resume", category: "setup", screens: ["upload"],
    title: "Uploading your résumé",
    body: [
      { p: "Upload the same résumé you would send to an employer, as a PDF or Word (.docx) file." },
      { list: [
        "Your résumé must be in English. A bilingual résumé works if at least half of it is in English.",
        "Names of employers, schools and job titles in other languages are fine.",
        "Before anything else happens, the [privacy scan](#privacy-scan) removes personal details. You then review the result.",
      ] },
      { note: "If your résumé is rejected as not in English, upload an English version." },
    ],
  },
  {
    id: "privacy-scan", category: "setup", screens: ["upload"],
    title: "What the privacy scan removes",
    body: [
      { p: "Before your résumé is used, the privacy scan looks for personal details and replaces them with neutral labels such as [Name] or [Company]." },
      { list: [
        "Contact details: email addresses, phone numbers, web and social media links.",
        "Your name and other people's names.",
        "Addresses and identification numbers.",
      ] },
      { p: "Your skills, experience and job titles stay, because the interview questions need them." },
      { note: "Always read the scanned text before you continue. The scan removes as much as it can, but it is not perfect, so review the scanned text and make any edits you need. Then select the checkbox to confirm that you have reviewed the text and approve it for use in your interview. See also [Your data and privacy](#data-privacy)." },
    ],
  },
  {
    id: "job-description", category: "setup", screens: ["upload"],
    title: "Real or sample job description",
    body: [
      { p: "For the most realistic practice, paste or upload the real job description for the role you are applying for." },
      { p: "If you are not applying for a specific role and simply want to practise your interview skills, choose a sample job description. You can pick a job category and level, or let the app create a sample job description that fits your résumé." },
      { p: "The interview questions combine your résumé with the job description to create a set of questions customized for your interview. If the résumé you upload and the job description have very little in common, you will see the [fit check warning](#fit-check)." },
    ],
  },
  {
    id: "fit-check", category: "setup", screens: ["upload"],
    title: "What the fit check warning means",
    body: [
      { p: "If your résumé and the job description have very little in common, you see a warning before the interview is built. Good practice questions need something from both." },
      { note: "The warning is not a judgement of whether you are right for the job. It only means the simulator may have difficulty creating questions that are relevant to your interview." },
      { p: "You can:" },
      { list: [
        "Upload a different job description.",
        "Let the app suggest a practice role that fits your résumé.",
        "Practise anyway.",
      ] },
      { p: "Seeing the warning and changing the job description does not affect your session count. Practise anyway does use a normal interview [session](#what-uses-session)." },
    ],
  },
  {
    id: "guided-or-live", category: "setup", screens: ["upload"],
    title: "Guided Mode or Advanced Mode: which to choose",
    body: [
      { p: "Both modes use the same questions and the same scoring. The difference is how the interview feels." },
      { list: [
        "Guided Mode: you can see each question, [replay it](#replay-question), record when you're ready, listen to your answer and record it again before you send it. See [Recording and finishing an answer](#guided-recording).",
        "Advanced Mode: like a real interview. Audio only, no text. You have 30 seconds to start answering, and the interviewer moves on when you stop speaking. See [How Advanced Mode works](#live-how).",
      ] },
      { p: "Start with Guided Mode if you are new to interviews in English. Choose Advanced Mode when you want to practise under real conditions." },
      { note: "The [free trial](#free-trial) uses Guided Mode. Advanced Mode comes with [30-day full access](#access-sessions)." },
    ],
  },
  {
    id: "choose-voice", category: "setup", screens: ["upload"],
    title: "Choosing the interviewer's voice",
    body: [
      { p: "Choose a female or male interviewer in Session settings. Select Hear this voice to listen to a short sample first." },
      { p: "The voice reads every question, follow-up question and comment in the interview. You can change the volume at any time with the volume control next to Help. See [No sound or the volume is too low](#no-sound)." },
    ],
  },

  // ── Microphone and sound ─────────────────────────────────────────────────
  {
    id: "check-sound", category: "sound", screens: ["upload", "warmup", "interview", "live"],
    title: "Check my microphone and sound",
    body: [
      { steps: [
        "It is best to use headphones if you can.",
        "On the setup screen, where you choose the male or female interviewer voice, select Hear this voice. If you hear nothing, see [No sound or the volume is too low](#no-sound).",
        "Use the [warm-up question](#warm-up) before the interview to record a few words and play them back.",
        "If your recording is silent, check that the right microphone is selected and [allowed in your browser](#allow-microphone).",
      ] },
      { note: "A quiet room makes a big difference, especially in [Advanced Mode](#live-how), where silence ends your answer." },
    ],
  },
  {
    id: "allow-microphone", category: "sound", screens: ["warmup", "interview", "live"],
    title: "Allowing the microphone in your browser",
    body: [
      { p: "The first time you record, your browser asks for permission to use the microphone. Select Allow." },
      { p: "If you blocked it by mistake:" },
      { steps: [
        "Select the lock or settings icon next to the web address.",
        "Find Microphone and change it to Allow.",
        "Reload the page.",
      ] },
      { note: "On a mobile device, also check that your browser is allowed to use the microphone in your device's settings. See also [Supported browsers and devices](#browsers)." },
    ],
  },
  {
    id: "no-sound", category: "sound", screens: ["upload", "interview", "live"],
    title: "No sound or the volume is too low",
    body: [
      { list: [
        "Check the volume control next to Help. If it displays a crossed-out speaker, it is muted: select it to unmute.",
        "Check your device volume and that your headphones are connected.",
        "If a question doesn't play in Guided Mode, select Play question. If the audio is unavailable, check your computer or device's system settings. See [Replaying a question](#replay-question).",
      ] },
    ],
  },
  {
    id: "browsers", category: "sound", screens: ["upload", "warmup", "live"],
    title: "Supported browsers and devices",
    body: [
      { p: "Use an up-to-date version of Chrome, Edge, Safari or Firefox. A computer gives the best experience, but tablets and phones work too." },
      { list: [
        "Your browser must be [allowed to use the microphone](#allow-microphone).",
        "Advanced Mode needs a browser that can record audio. If yours can't, you are offered Guided Mode instead.",
        "Close other apps that might be using the microphone, such as video calls.",
      ] },
    ],
  },

  // ── During the interview ─────────────────────────────────────────────────
  {
    id: "warm-up", category: "during", screens: ["warmup"],
    title: "The warm-up question",
    body: [
      { p: "Before the interview starts, you answer one easy warm-up question. It helps you [test your microphone](#check-sound) and feel comfortable speaking." },
      { p: "Record your answer and play it back to check the sound. Nothing from the warm-up is sent for analysis or included in your report." },
      { p: "When you're ready, select Continue to the interview." },
    ],
  },
  {
    id: "guided-recording", category: "during", screens: ["interview"],
    title: "Recording and finishing an answer (Guided Mode)",
    body: [
      { steps: [
        "Listen to the question. You can [replay it](#replay-question).",
        "Select the microphone to start recording, and speak your answer.",
        "Select it again to stop. You can listen to your recording.",
        "Select Re-record to try again, or Submit answer to send it.",
      ] },
      { p: "After the last question, select See my report." },
      { note: "Answer as you would in a real interview: one clear example, what you did, and how it turned out." },
    ],
  },
  {
    id: "replay-question", category: "during", screens: ["interview"],
    title: "Replaying a question",
    body: [
      { p: "In Guided Mode, select Play question to hear it again, or Replay slower to hear it more slowly." },
      { p: "If the question text is hidden, select Reveal the question text to read it." },
      { p: "In Advanced Mode, there is no replay button: [ask the interviewer to repeat](#live-repeat), for example \"Could you repeat the question, please?\"" },
    ],
  },
  {
    id: "follow-ups", category: "during", screens: ["interview", "live", "report"],
    title: "Follow-up questions",
    body: [
      { p: "Like a real interviewer, the simulator may ask one short follow-up question when something important is missing from your answer, for example a specific example or how things turned out." },
      { list: [
        "Your answer to the follow-up is scored together with your main answer.",
        "A follow-up is not a penalty, but answering fully the first time is a strong interview habit. Your report counts these as [Complete on the first answer](#complete-first).",
      ] },
    ],
  },
  {
    id: "restart-or-end", category: "during", screens: ["warmup", "interview"],
    title: "Restart or End: what each does",
    body: [
      { list: [
        "Restart starts the same interview again from Question 1 and deletes your answers so far. Your résumé, job description and settings are kept. The first restart of an interview is free; each later restart uses one [session](#what-uses-session).",
        "End Interview stops the interview and deletes your answers and progress. The session it used is not reset.",
      ] },
      { note: "If you only need a break, you don't need to end the interview. Your interview stays active for 60 minutes, as long as you leave the app open in your browser." },
    ],
  },
  {
    id: "prep-failed", category: "during", screens: ["prepare"],
    title: "If preparation fails",
    body: [
      { p: "Building your questions usually takes under a minute. If you see \"We couldn't prepare your interview\", select Try again." },
      { p: "If it keeps happening, the cause may be on our servers. Wait a few minutes and try again, or contact support so we can look at it." },
    ],
  },

  // ── Advanced Mode ────────────────────────────────────────────────────────
  {
    id: "live-how", category: "live", screens: ["live", "upload"],
    title: "How Advanced Mode works",
    body: [
      { p: "Advanced Mode feels more like a real interview: audio only, no question text, no buttons to press between questions." },
      { steps: [
        "The interviewer asks the question.",
        "Your microphone opens and a [countdown](#live-countdown) shows how long you have to start answering.",
        "Speak your answer. There is no time limit once you start.",
        "When you stop speaking, the interviewer moves on. See [Ending an answer](#live-ending).",
      ] },
      { note: "Use headphones in a quiet room. Help is available between questions, not while a question is playing or while you are answering. Not sure which mode to choose? See [Guided Mode or Advanced Mode](#guided-or-live)." },
    ],
  },
  {
    id: "live-countdown", category: "live", screens: ["live"],
    title: "The 30-second start countdown",
    body: [
      { p: "After each question, you have 30 seconds to start answering (20 seconds for a [follow-up question](#follow-ups)). The countdown stops as soon as you start speaking." },
      { p: "If you don't start in time, the interviewer says \"Let's move on\" and goes to the next question. Your report shows \"No answer given\" for that question, and it counts as unanswered." },
      { note: "Take a moment to think, then start speaking. Once you start, keep going: about 3 seconds of silence [ends your answer](#live-ending)." },
    ],
  },
  {
    id: "live-ending", category: "live", screens: ["live"],
    title: "Ending an answer and \"I'm finished\"",
    body: [
      { p: "Your answer ends when you stop speaking for about 3 seconds, or when you select I'm finished." },
      { list: [
        "Short pauses while you think are fine: the first seconds of an answer never end it.",
        "Select I'm finished if background noise keeps the microphone listening after you have finished.",
        "Everything you said is scored.",
      ] },
    ],
  },
  {
    id: "live-repeat", category: "live", screens: ["live"],
    title: "Asking the interviewer to repeat",
    body: [
      { p: "Just ask, as you would in a real interview:" },
      { list: [
        "\"Could you repeat the question, please?\"",
        "\"Sorry, I didn't understand the question.\"",
      ] },
      { p: "The interviewer repeats the question in the same words, and the [start countdown](#live-countdown) begins again. Asking for a repeat does not count as an answer." },
    ],
  },

  // ── Your report and scores ───────────────────────────────────────────────
  {
    id: "report-overview", category: "report", screens: ["report", "trial-report"],
    title: "Understanding your report",
    body: [
      { p: "Your report has two headline scores out of 100:" },
      { list: [
        "[Interview Performance](#interview-performance): how well your answers work as interview answers.",
        "[Professional English Communication](#english-dimensions): how well you communicate in English.",
      ] },
      { p: "Below them, each question shows its scores, what you said, any [follow-up](#follow-ups), a [polished version of your answer](#polished-answer), what you did well and what to improve." },
      { p: "The bars are green from 75, amber from 50 to 74, and red below 50." },
      { note: "Scores and English levels are practice estimates to guide your preparation. They are not a certification." },
    ],
  },
  {
    id: "interview-performance", category: "report", screens: ["report", "trial-report"],
    title: "Interview Performance: the six dimensions",
    body: [
      { list: [
        "Relevance to the question: did you answer what was asked?",
        "Supporting evidence: did you give a real, specific example?",
        "Organization and structure: was the answer easy to follow?",
        "Professional competency: did it show the skills the job needs?",
        "Evidence of results: did you say how things turned out? Shown as \"—\" when the question didn't ask for a result.",
        "Professional presence: did you sound confident and take ownership?",
      ] },
      { p: "Your Interview Performance score is the average of these dimensions. See also [Understanding your report](#report-overview)." },
    ],
  },
  {
    id: "english-dimensions", category: "report", screens: ["report", "trial-report"],
    title: "Professional English: the five dimensions and CEFR levels",
    body: [
      { p: "Your English is measured on five dimensions: grammar, vocabulary, clarity, professional tone and fluency." },
      { p: "Each is assessed using guidelines similar to those of the CEFR, the international scale for language levels, and turned into a score: around 45 is similar to B1, 65 is similar to B2 and 85 is similar to C1." },
      { list: [
        "Strong: 80 and above.",
        "Functional: 60 to 79.",
        "Developing: below 60.",
      ] },
      { p: "If an answer is too short to judge your English fairly, it shows \"Answer too short to assess English\" and is left out of your English score. See also [Understanding your report](#report-overview)." },
    ],
  },
  {
    id: "complete-first", category: "report", screens: ["report"],
    title: "\"Complete on the first answer\"",
    body: [
      { p: "This counts the questions you answered fully the first time, without needing a [follow-up question](#follow-ups)." },
      { p: "In a real interview, answers that already include a specific example and how it turned out make a strong impression. Use this number to see how often your first answer was complete." },
    ],
  },
  {
    id: "polished-answer", category: "report", screens: ["report", "trial-report"],
    title: "Using the polished answer and its suggestions",
    body: [
      { p: "The polished answer is your own answer rewritten in natural, professional English. It keeps your meaning and adds nothing you didn't say." },
      { p: "The bold, coloured suggestions in square brackets show what a stronger answer would add, for example a result or a specific example." },
      { note: "Replace each suggestion with real details from your own experience, then practise saying the whole answer out loud. You can try again with [Re-Practice](#re-practice)." },
    ],
  },
  {
    id: "re-practice", category: "report", screens: ["report"],
    title: "Re-Practice",
    body: [
      { p: "Re-Practice lets you answer the same questions again, once, to apply what you learned from your report." },
      { list: [
        "It uses one [session](#what-uses-session).",
        "It is offered on your report page right after you finish the interview, and closes when you leave that page or log out.",
        "Your report compares your new scores with your first attempt.",
      ] },
      { p: "To practise with new questions, set up a new interview with the same résumé and job description." },
    ],
  },
  {
    id: "download-report", category: "report", screens: ["report", "downloads"],
    title: "Downloading your report",
    body: [
      { p: "Select Download Report on your report page to save it as a PDF. You can also download it later from My Reports & Downloads in your account menu." },
      { p: "The PDF includes your scores, a page of English feedback, and every question with your answer, the polished answer and what to improve." },
      { note: "Reports can be downloaded until your 30-day access ends. See [How long your reports are kept](#report-retention)." },
    ],
  },

  // ── Account, sessions and privacy ────────────────────────────────────────
  {
    id: "access-sessions", category: "account", screens: ["account", "upload"],
    title: "Your 30-day access and 10 sessions",
    body: [
      { p: "Full access lasts 30 days and includes a total of up to 10 interview sessions, which you can use on any days during the 30-day access period." },
      { list: [
        "Sessions used: shown in the top bar, for example \"Sessions used: 3 of 10\". Hold your pointer over it to see when your access ends.",
        "Unused sessions end with your 30-day access.",
        "Renewing starts a new 30 days with 10 new sessions. Access does not renew automatically: to renew, you purchase another 30 days of access.",
      ] },
      { p: "See [What uses a session](#what-uses-session)." },
    ],
  },
  {
    id: "what-uses-session", category: "account", screens: ["account", "interview"],
    title: "What uses a session",
    body: [
      { list: [
        "Starting a new interview, in Guided Mode or Advanced Mode: 1 session.",
        "[Re-Practice](#re-practice): 1 session.",
        "Restarting an interview: the first restart is free; each later restart uses 1 session. See [Restart or End](#restart-or-end).",
        "If an interview fails to start, its session is reset.",
        "If [preparing your customized interview](#prep-failed) fails, contact support. We will reset your session count and look into the cause.",
      ] },
      { p: "Reading your report and downloading it never use a session." },
    ],
  },
  {
    id: "report-retention", category: "account", screens: ["account", "downloads", "report"],
    title: "How long your reports are kept",
    body: [
      { p: "Your report, and the résumé, job description and answers it was built from, are kept until the end of your 30-day access period. Then they are all deleted automatically." },
      { p: "We email you about 48 hours before your reports are removed, so you can [download the PDF](#download-report). Once your 30-day access has ended, you can no longer download your previous reports." },
    ],
  },
  {
    id: "data-privacy", category: "account", screens: ["account", "upload"],
    title: "Your data and privacy",
    body: [
      { list: [
        "Your uploaded résumé goes through a [privacy scan](#privacy-scan) before it is used, and you review the result. The app never uses your original résumé to create the interview: only the version you approve after the scan is used, and it is kept until your 30-day access ends.",
        "Your scanned résumé text, job description and answers are used only to build and score your practice interview.",
        "They are deleted at the end of your 30-day access period (after 24 hours for a [free trial](#free-deleted)).",
        "AI services process your interview to create questions, transcribe your answers and give feedback.",
      ] },
      { p: "For full details, see the Privacy Policy." },
    ],
  },

  // ── Free trial ───────────────────────────────────────────────────────────
  {
    id: "free-trial", category: "free", screens: ["upload", "trial-report"],
    title: "What the free trial includes",
    body: [
      { list: [
        "One free practice interview of 6 questions, in Guided Mode.",
        "Your full results on screen, with feedback for every question.",
        "The same résumé [privacy scan](#privacy-scan) as full access.",
      ] },
      { p: "Downloading the report, [Re-Practice](#re-practice), restart and [Advanced Mode](#live-how) come with [30-day full access](#access-sessions). You can see them in the app, greyed out." },
    ],
  },
  {
    id: "free-deleted", category: "free", screens: ["trial-report"],
    title: "Why your free results are deleted",
    body: [
      { p: "Free trial results are not saved. They are deleted when you leave the results page or log out, and automatically 24 hours after the interview." },
      { p: "Read your feedback before you leave the page. With full access, reports are kept until your 30-day access ends and can be [downloaded as a PDF](#download-report)." },
    ],
  },
];
